import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { API_BASE_URL } from '../config/app-settings.tokens';
import { Folder, FileItem } from '../models/file-management.model';
import { NotificationService } from './notification.service';
import { switchMap } from 'rxjs/operators';

/**
 * File details response
 */
export interface FileDetailsResponse {
  success: boolean;
  message: string;
  data: FileItem;
  errors: string[] | null;
}

/**
 * File with breadcrumb response
 */
export interface FileWithBreadcrumbResponse {
  success: boolean;
  message: string;
  data: {
    file: FileItem;
    breadcrumb: Folder[];
    parentFolder: {
      id: number;
      nameEn: string;
      nameAr: string;
      descriptionEn?: string;
      descriptionAr?: string;
    };
  };
  errors: string[] | null;
}

@Injectable({
  providedIn: 'root'
})
export class FileNavigationService {
  private readonly baseUrl = `${inject(API_BASE_URL)}/api/v1/external`;
  private readonly loggingPrefix = '[FileNavigationService]';

  // Subject to emit file navigation events
  private fileNavigationSubject = new Subject<{
    fileId: number;
    file: FileItem;
    breadcrumb: Folder[];
    parentFolderId: number;
  }>();
  public fileNavigation$ = this.fileNavigationSubject.asObservable();

  // Subject to emit folder navigation events (e.g., sidebar tree selection)
  private folderSelectionSubject = new Subject<{ folder: Folder; breadcrumb: Folder[] }>();
  public folderSelection$ = this.folderSelectionSubject.asObservable();

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService
  ) {}

  /**
   * Get file details with breadcrumb (parent folders)
   * GET /externalbackend/api/v1/external/files/{fileId}/breadcrumb?iamUserId={iamUserId}
   *
   * @param fileId File ID to retrieve
   * @returns Observable<FileWithBreadcrumbResponse>
   */
  getFileWithBreadcrumb(fileId: number): Observable<FileWithBreadcrumbResponse> {
    return this.notificationService.ensureUserInitialized().pipe(
      switchMap((iamUserId) => {
        const params = new HttpParams()
          .set('iamUserId', iamUserId);

        return this.http.get<FileWithBreadcrumbResponse>(
          `${this.baseUrl}/files/${fileId}/breadcrumb`,
          { params }
        ).pipe(
          tap((response) => {
          }),
          catchError((error) => {
            return throwError(() => error);
          })
        );
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Navigate to file from notification
   * Fetches file details with breadcrumb and emits navigation event
   *
   * @param fileId File ID from notification metadata
   */
  navigateToFile(fileId: number): void {
    console.log(`${this.loggingPrefix} Starting file navigation for fileId:`, fileId);
    
    this.getFileWithBreadcrumb(fileId).subscribe({
      next: (response) => {
        console.log(`${this.loggingPrefix} File details retrieved:`, response);
        
        if (response.success) {
          const file = response.data.file;
          const breadcrumb = response.data.breadcrumb;
          const parentFolderId = response.data.parentFolder.id;

          console.log(`${this.loggingPrefix} Emitting navigation event:`, {
            fileId,
            fileName: file.titleEn,
            parentFolderId,
            breadcrumbLength: breadcrumb.length
          });

          // Emit navigation event with all necessary data
          this.fileNavigationSubject.next({
            fileId,
            file,
            breadcrumb,
            parentFolderId
          });
        } else {
          console.warn(`${this.loggingPrefix} File retrieval returned success=false:`, response);
        }
      },
      error: (err: any) => {
        console.error(`${this.loggingPrefix} Failed to navigate to file ${fileId}:`, err);
      }
    });
  }

  /**
   * Emit a folder selection event to synchronize sidebar tree with main file explorer
   */
  selectFolder(folder: Folder, breadcrumb: Folder[]): void {
    this.folderSelectionSubject.next({ folder, breadcrumb });
  }
}
